// src/components/MatchDay/MatchDay.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import API_BASE_URL from "../../config/config.js";
import dayjs from "dayjs";
import "dayjs/locale/es";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { stateMatch } from "../../utils/stateMatch.js";
import ModalDetalle from "../Modal/ModalDetalle/ModalDetalle";
import { RiLiveFill } from "react-icons/ri";
import useMatchSnapshotsSSE from "../../hooks/useMatchSnapshotsSSE.js";
import { centralDateStreamUrl } from "../../services/live/matchSnapshotSseClient.js";
import { applyMatchSnapshot, mergeMatchSnapshot } from "../../utils/applyMatchSnapshot.js";

dayjs.extend(customParseFormat);
dayjs.locale("es");

const YEAR_SELECTED = 2026;
const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

const VOLLEYBALL_SPORT_IDS = new Set([
  16,
  17,
  100,
  101,
  102,
  103,
  104,
  999,
  113,
  114,
  115,
  116,
]);

const convertTo12HourFormat = (time) => {
  if (!time) return "Hora";

  const [hour, minutes = "00"] = String(time).split(":");
  const hourInt = parseInt(hour, 10);

  if (Number.isNaN(hourInt)) return "Hora";

  const period = hourInt >= 12 ? "pm" : "am";
  const hour12 = hourInt % 12 || 12;
  return `${hour12}:${minutes}${period}`;
};

const formatDateForApi = (date) => {
  if (!date) return dayjs().format("YYYY-MM-DD");

  if (ISO_DATE_PATTERN.test(String(date))) {
    return date;
  }

  const parsedDate = dayjs(`${date} ${dayjs().year()}`, "DD MMM YYYY", "es", true);

  return parsedDate.isValid()
    ? parsedDate.format("YYYY-MM-DD")
    : dayjs(date).format("YYYY-MM-DD");
};

const getVenueName = (match) => match?.sede?.name || "Sede por definir";

const getInstitutionLogo = (institution) => {
  if (!institution?.path_base || !institution?.image_path) {
    return "/assets/icon/shield_icon.png";
  }

  return `${institution.path_base}${institution.image_path}`;
};

const MatchDay = ({ selectedDate, onDateChange }) => {
  const [matches, setMatches] = useState([]);
  const [sports, setSports] = useState({});
  const [openDetalle, setOpenDetalle] = useState(false);
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [selectedSports, setSelectedSports] = useState({});
  const [loading, setLoading] = useState(false);
  const [campeonatos, setCampeonatos] = useState({});
  const [moodName] = useState(1);
  const [selectedVenues, setSelectedVenues] = useState({});
  const [selectedEventId, setSelectedEventId] = useState("");
  const [liveRevision, setLiveRevision] = useState(0);
  const lastLoadedDateRef = useRef(null);

  const formattedSelectedDate = useMemo(
    () => formatDateForApi(selectedDate),
    [selectedDate]
  );

  const getSport = async (idsport) => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/sport?idsport=${idsport}`
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching sport:", error);
      return null;
    }
  };

  useEffect(() => {
    let ignoreRequest = false;

    const getMatches = async () => {
      setLoading(true);

      try {
        const response = await axios.post(`${API_BASE_URL}/getmatchesperiod`, {
          date_match: formattedSelectedDate,
        });

        if (ignoreRequest) return;

        let fetchedMatches = Array.isArray(response.data)
          ? response.data.filter((match) => !match.flag_bye)
          : [];

        fetchedMatches = fetchedMatches.sort((a, b) => {
          const timeA = a.time_match || "";
          const timeB = b.time_match || "";
          return timeA.localeCompare(timeB);
        });

        setMatches(fetchedMatches);

        const sportsData = {};
        const sportIds = Array.from(
          new Set(fetchedMatches.map((match) => match.idsport).filter(Boolean))
        );

        await Promise.all(
          sportIds.map(async (idsport) => {
            const sportData = await getSport(idsport);
            if (sportData) {
              sportsData[idsport] = sportData;
            }
          })
        );

        if (ignoreRequest) return;

        setSports(sportsData);

        // Solo se limpian filtros cuando cambia realmente el día, no al resincronizar SSE.
        if (lastLoadedDateRef.current !== formattedSelectedDate) {
          setSelectedEventId("");
          setSelectedSports({});
          setSelectedVenues({});
        }
        lastLoadedDateRef.current = formattedSelectedDate;
      } catch (error) {
        if (!ignoreRequest) {
          console.error("Error fetching matches:", error);
          setMatches([]);
          setSports({});
          setSelectedEventId("");
          setSelectedSports({});
          setSelectedVenues({});
        }
      } finally {
        if (!ignoreRequest) {
          setLoading(false);
        }
      }
    };

    getMatches();

    return () => {
      ignoreRequest = true;
    };
  }, [formattedSelectedDate, liveRevision]);

  const handleLiveSnapshot = useCallback((snapshot) => {
    setMatches((current) => applyMatchSnapshot(current, snapshot));
    setSelectedMatch((current) =>
      current ? mergeMatchSnapshot(current, snapshot) : current
    );
  }, []);

  useMatchSnapshotsSSE({
    streamUrl: centralDateStreamUrl(API_BASE_URL, formattedSelectedDate),
    enabled: Boolean(formattedSelectedDate),
    onSnapshot: handleLiveSnapshot,
    onResync: () => setLiveRevision((value) => value + 1),
  });

  useEffect(() => {
    const getCampeonatos = async () => {
      try {
        const response = await axios.get(
          `${API_BASE_URL}/championships/?periodo=${YEAR_SELECTED}`
        );

        const campeonatosMap = Array.isArray(response.data)
          ? response.data.reduce((acc, championship) => {
              acc[championship.idchampionship] = championship;
              return acc;
            }, {})
          : {};

        setCampeonatos(campeonatosMap);
      } catch (error) {
        console.error("Error fetching championships:", error);
        setCampeonatos({});
      }
    };

    getCampeonatos();
  }, []);

  // Al cambiar evento, se limpian filtros para que no queden deportes/sedes de otro evento.
  useEffect(() => {
    setSelectedSports({});
    setSelectedVenues({});
  }, [selectedEventId]);

  const handleSportSelection = (idsport) => {
    const sportKey = String(idsport);

    setSelectedSports((prev) => ({
      ...prev,
      [sportKey]: !prev[sportKey],
    }));
  };

  const handleVenueSelection = (venue) => {
    setSelectedVenues((prev) => ({
      ...prev,
      [venue]: !prev[venue],
    }));
  };

  const handleOpenDetalle = (match) => {
    setSelectedMatch(match);
    setOpenDetalle(true);
  };

  const availableEvents = useMemo(() => {
    const eventMap = new Map();

    matches.forEach((match) => {
      const eventId = String(match.idevent || "");
      if (!eventId) return;

      if (!eventMap.has(eventId)) {
        eventMap.set(eventId, {
          id: eventId,
          name: campeonatos[eventId]?.name || `Evento ${eventId}`,
          totalMatches: 0,
        });
      }

      eventMap.get(eventId).totalMatches += 1;
    });

    return Array.from(eventMap.values()).sort((a, b) =>
      a.name.localeCompare(b.name)
    );
  }, [campeonatos, matches]);

  const eventScopedMatches = useMemo(() => {
    if (!selectedEventId) return matches;

    return matches.filter(
      (match) => String(match.idevent) === String(selectedEventId)
    );
  }, [matches, selectedEventId]);

  const availableSports = useMemo(() => {
    const sportIds = Array.from(
      new Set(eventScopedMatches.map((match) => match.idsport).filter(Boolean))
    );

    return sportIds
      .map((idsport) => sports[idsport])
      .filter(Boolean)
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [eventScopedMatches, sports]);

  const availableVenues = useMemo(() => {
    return Array.from(
      new Set(
        eventScopedMatches
          .map((match) => match.sede?.name)
          .filter(Boolean)
      )
    ).sort((a, b) => a.localeCompare(b));
  }, [eventScopedMatches]);

  const anySportSelected = Object.values(selectedSports).some(Boolean);
  const anyVenueSelected = Object.values(selectedVenues).some(Boolean);

  const filteredMatches = useMemo(() => {
    return eventScopedMatches.filter((match) => {
      const sportKey = String(match.idsport);
      const passSport = anySportSelected ? selectedSports[sportKey] : true;

      const venueName = match.sede?.name;
      const passVenue = anyVenueSelected ? !!selectedVenues[venueName] : true;

      return passSport && passVenue;
    });
  }, [
    anySportSelected,
    anyVenueSelected,
    eventScopedMatches,
    selectedSports,
    selectedVenues,
  ]);

  const groupedFilteredMatches = useMemo(() => {
    const grouped = {};

    filteredMatches.forEach((match) => {
      if (!grouped[match.idevent]) grouped[match.idevent] = [];
      grouped[match.idevent].push(match);
    });

    return grouped;
  }, [filteredMatches]);

  const selectedEventLabel = selectedEventId
    ? availableEvents.find((event) => event.id === String(selectedEventId))?.name ||
      `Evento ${selectedEventId}`
    : "Todos los eventos";

  const hasMatches = matches.length > 0;
  const hasFilteredMatches = filteredMatches.length > 0;

  return (
    <>
      {loading ? (
        <div className="mt-4 flex justify-center rounded-2xl border border-gray-100 bg-white px-4 py-6 shadow-sm">
          <span className="text-base font-semibold text-gray-700 sm:text-lg">
            Cargando enfrentamientos para esta fecha...
          </span>
        </div>
      ) : (
        <div className="mt-4 space-y-5">
          <div className="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm sm:p-5">
            <div className="grid gap-4 lg:grid-cols-2">
              <div>
                <label className="mb-1 block text-xs font-bold uppercase tracking-widest text-gray-400">
                  Evento del día
                </label>
                <select
                  value={selectedEventId}
                  onChange={(event) => setSelectedEventId(event.target.value)}
                  disabled={!hasMatches}
                  className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm font-semibold text-gray-700 outline-none transition focus:border-indigo-400 focus:bg-white focus:ring-4 focus:ring-indigo-50 disabled:cursor-not-allowed disabled:text-gray-400"
                >
                  <option value="">Todos los eventos</option>
                  {availableEvents.map((event) => (
                    <option key={event.id} value={event.id}>
                      {event.name} ({event.totalMatches})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="mb-1 block text-xs font-bold uppercase tracking-widest text-gray-400">
                  Ir directo a un día
                </label>
                <input
                  type="date"
                  value={formattedSelectedDate}
                  onChange={(event) => onDateChange(event.target.value)}
                  className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm font-semibold text-gray-700 outline-none transition focus:border-indigo-400 focus:bg-white focus:ring-4 focus:ring-indigo-50"
                />
              </div>
            </div>

            <div className="mt-4 flex flex-wrap items-center gap-2 text-xs text-gray-500">
              <span className="rounded-full bg-indigo-50 px-3 py-1 font-bold text-indigo-600">
                {selectedEventLabel}
              </span>
              <span className="rounded-full bg-gray-100 px-3 py-1 font-semibold text-gray-600">
                {eventScopedMatches.length} partido{eventScopedMatches.length === 1 ? "" : "s"}
              </span>
              {anySportSelected && (
                <button
                  type="button"
                  onClick={() => setSelectedSports({})}
                  className="rounded-full bg-amber-50 px-3 py-1 font-semibold text-amber-700 transition hover:bg-amber-100"
                >
                  Limpiar deportes
                </button>
              )}
              {anyVenueSelected && (
                <button
                  type="button"
                  onClick={() => setSelectedVenues({})}
                  className="rounded-full bg-amber-50 px-3 py-1 font-semibold text-amber-700 transition hover:bg-amber-100"
                >
                  Limpiar sedes
                </button>
              )}
            </div>

            {hasMatches && (
              <div className="mt-5 grid gap-5 lg:grid-cols-2">
                <div>
                  <h3 className="mb-2 text-sm font-extrabold text-gray-700">
                    Filtrar por deporte
                  </h3>
                  {availableSports.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {availableSports.map((sport) => {
                        const sportKey = String(sport.idsport);
                        const isSelected = !!selectedSports[sportKey];

                        return (
                          <button
                            type="button"
                            key={sport.idsport}
                            onClick={() => handleSportSelection(sport.idsport)}
                            className={`rounded-full border px-3 py-2 text-xs font-bold transition sm:text-sm ${
                              isSelected
                                ? "border-indigo-600 bg-indigo-600 text-white shadow-sm shadow-indigo-100"
                                : "border-gray-200 bg-white text-gray-600 hover:border-indigo-200 hover:bg-indigo-50 hover:text-indigo-700"
                            }`}
                          >
                            {sport.name} {sport.acronym ? `(${sport.acronym})` : ""}
                          </button>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-sm font-semibold text-gray-400">
                      No hay deportes disponibles para este evento.
                    </p>
                  )}
                </div>

                <div>
                  <h3 className="mb-2 text-sm font-extrabold text-gray-700">
                    Filtrar por sede
                  </h3>
                  {availableVenues.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {availableVenues.map((venue) => {
                        const isSelected = !!selectedVenues[venue];

                        return (
                          <button
                            type="button"
                            key={venue}
                            onClick={() => handleVenueSelection(venue)}
                            className={`rounded-full border px-3 py-2 text-xs font-bold transition sm:text-sm ${
                              isSelected
                                ? "border-slate-800 bg-slate-800 text-white shadow-sm shadow-slate-100"
                                : "border-gray-200 bg-white text-gray-600 hover:border-slate-200 hover:bg-slate-50 hover:text-slate-800"
                            }`}
                          >
                            {venue}
                          </button>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-sm font-semibold text-gray-400">
                      No hay sedes disponibles para este evento.
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>

          {!hasMatches && (
            <div className="rounded-2xl border border-gray-100 bg-white p-6 shadow-sm">
              <div className="text-center text-xl font-semibold text-gray-600">
                No hay enfrentamientos programados para este día.
              </div>
            </div>
          )}

          {hasMatches && !hasFilteredMatches && (
            <div className="rounded-2xl border border-gray-100 bg-white p-6 shadow-sm">
              <div className="text-center text-lg font-semibold text-gray-600">
                No hay enfrentamientos que coincidan con los filtros.
              </div>
            </div>
          )}

          {hasFilteredMatches &&
            Object.entries(groupedFilteredMatches).map(([eventId, eventMatches]) => (
              <div
                key={eventId}
                className="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm sm:p-6"
              >
                <div className="mb-6 text-center">
                  <p className="text-xs font-bold uppercase tracking-widest text-indigo-400">
                    Evento
                  </p>
                  <h2 className="mt-1 text-xl font-extrabold uppercase tracking-widest text-gray-800 sm:text-2xl">
                    {campeonatos[eventId]?.name || `Evento ${eventId}`}
                  </h2>
                </div>

                <div className="space-y-10">
                  {eventMatches.map((match) => (
                    <div
                      key={match.idmatch}
                      className="relative cursor-pointer rounded-xl border-t border-gray-200 px-2 pt-7 transition-all duration-300 hover:bg-gray-50 sm:px-3"
                      onClick={() => handleOpenDetalle(match)}
                    >
                      <div className="absolute left-2 top-1 sm:left-3">
                        {/* FTR-0028 D-31: esta rama existe para el encabezado "sin equipos", que
                            anuncia el partido por su `tag_final`. Un partido del Centro de
                            Programación en fase de grupos también llega con los dos lados en null,
                            pero NO tiene `tag_final`, y sin esta condición imprimía
                            "Fútbol | 10:00 |  | Sede" con un separador vacío. Exigir la etiqueta
                            deja el encabezado normal para esos partidos y no cambia nada para los
                            partidos de fase final que ya usaban esta rama. */}
                        {sports[match.idsport] &&
                        match.Participant1 === null &&
                        match.Participant2 === null &&
                        String(match.tag_final || "").trim() !== "" &&
                        sports[match.idsport]?.typeSport !== 3 ? (
                          <>
                            <p className="hidden text-sm text-indigo-500 sm:block sm:text-lg">
                              {`${sports[match.idsport].name} | ${
                                match.time_match
                                  ? convertTo12HourFormat(match.time_match)
                                  : "Hora"
                              } | ${match.tag_final || ""} | ${getVenueName(match)}`}
                            </p>
                            <p className="block text-lg text-indigo-500 sm:hidden">
                              {`${sports[match.idsport].acronym} | ${
                                match.time_match
                                  ? convertTo12HourFormat(match.time_match)
                                  : "Hora"
                              } | ${match.tag_final || ""} | ${getVenueName(match)}`}
                            </p>
                          </>
                        ) : (
                          <>
                            <p className="hidden text-sm text-indigo-500 sm:block sm:text-lg">
                              {sports[match.idsport]
                                ? `${sports[match.idsport].name} | ${
                                    match.time_match
                                      ? convertTo12HourFormat(match.time_match)
                                      : "Hora"
                                  } | ${getVenueName(match)} | ${match?.tag_final || ""}`
                                : "Cargando deporte..."}
                            </p>
                            <p className="block text-lg text-indigo-500 sm:hidden">
                              {sports[match.idsport]
                                ? `${sports[match.idsport].acronym} | ${
                                    match.time_match
                                      ? convertTo12HourFormat(match.time_match)
                                      : ""
                                  } ${getVenueName(match)}`
                                : "Cargando deporte..."}
                            </p>
                          </>
                        )}
                      </div>

                      <div className="relative flex items-center justify-center space-x-4 text-center sm:space-x-8">
                        {sports[match.idsport]?.typeSport === 3 ? (
                          <div className="text-sm font-semibold text-gray-800 sm:text-lg">
                            {match.tag_final}
                          </div>
                        ) : (
                          <>
                            <div className="flex w-28 items-center justify-center space-x-2 sm:w-36">
                              <span className="text-sm font-semibold text-gray-800 sm:text-lg">
                                {moodName === 1
                                  ? match.Participant1?.Institution?.abrev || "Por Definir"
                                  : match.Participant1?.Institution?.name || "Por Definir"}
                              </span>
                              <img
                                src={getInstitutionLogo(match.Participant1?.Institution)}
                                alt="logo 1"
                                className="h-10 w-10 rounded-full border border-gray-300 object-cover"
                              />
                            </div>

                            <div className="flex w-28 items-center justify-center sm:w-36">
                              <span className="mx-2 whitespace-nowrap text-2xl font-bold text-indigo-600 sm:mx-4 sm:text-4xl">
                                {VOLLEYBALL_SPORT_IDS.has(Number(match.idsport)) ? (
                                  <div className="flex items-center">
                                    <div className="score mx-2 flex text-2xl font-bold text-indigo-600 sm:mx-4 sm:text-4xl">
                                      <div className="mr-1 text-center">
                                        {match.periods &&
                                          match.periods.map((period, idx) => (
                                            <div key={idx} className="text-xs text-gray-500">
                                              {period.puntos1 === 0 && period.puntos2 === 0
                                                ? null
                                                : period.puntos1}
                                            </div>
                                          ))}
                                      </div>
                                      {match.sets1}
                                    </div>
                                    <span className="text-xl font-bold">vs</span>
                                    <div className="score mx-2 flex text-2xl font-bold text-indigo-600 sm:mx-4 sm:text-4xl">
                                      {match.sets2}
                                      <div className="ml-1 text-center">
                                        {match.periods &&
                                          match.periods.map((period, idx) => (
                                            <div key={idx} className="text-xs text-gray-500">
                                              {period.puntos1 === 0 && period.puntos2 === 0
                                                ? null
                                                : period.puntos2}
                                            </div>
                                          ))}
                                      </div>
                                    </div>
                                  </div>
                                ) : (
                                  <>
                                    {match.resultado1 ?? "0"} - {match.resultado2 ?? "0"}
                                  </>
                                )}
                              </span>
                            </div>

                            <div className="flex w-28 items-center justify-center space-x-2 sm:w-36">
                              <img
                                src={getInstitutionLogo(match.Participant2?.Institution)}
                                alt="logo 2"
                                className="h-10 w-10 rounded-full border border-gray-300 object-cover"
                              />
                              <span className="text-sm font-semibold text-gray-800 sm:text-lg">
                                {moodName === 1
                                  ? match.Participant2?.Institution?.abrev || "Por Definir"
                                  : match.Participant2?.Institution?.name || "Por Definir"}
                              </span>
                            </div>

                            <div
                              className={`absolute -top-6 right-0 flex items-center space-x-2 rounded-full px-3 py-1 text-xs font-semibold ${stateMatch(match.state).color}`}
                            >
                              {match.state === 2 && (
                                <RiLiveFill className="animate-pulse text-red-600" />
                              )}
                              <span>{stateMatch(match.state).tag}</span>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
        </div>
      )}

      {openDetalle && selectedMatch && (
        <ModalDetalle
          open={openDetalle}
          setOpen={setOpenDetalle}
          match={selectedMatch}
        />
      )}
    </>
  );
};

export default MatchDay;
